import React, { useState, useEffect } from 'react';
import TeacherComprehensionView from './ComprehensionResults';
import '../styles/ComprehensionView.scss';
import logger from '../utils/logger';
import LoadingAnecdotes from './LoadingAnecdotes';
import { track } from '@vercel/analytics';

function ComprehensionView({ API_BASE_URL, authToken, userData, onSubmit, initialComprehension, onBack, onSubmissionComplete }) {
  const [comprehensions, setComprehensions] = useState([]);
  const [selectedComprehension, setSelectedComprehension] = useState(initialComprehension || null);
  const [answers, setAnswers] = useState({});
  const [isLoading, setIsLoading] = useState(!initialComprehension);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [imageFiles, setImageFiles] = useState({});
  const [processingImages, setProcessingImages] = useState({});
  const [submissionId, setSubmissionId] = useState(null);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState('');
  // State for collapsible sections
  const [isPastExamsCollapsed, setIsPastExamsCollapsed] = useState(false);
  const [isTeacherExamsCollapsed, setIsTeacherExamsCollapsed] = useState(false);

  // Determine if the user is from the UK
  const isUKUser = userData?.country === 'UK';
  logger.debug('ComprehensionView - isUKUser:', isUKUser, 'based on country:', userData?.country);

  // Fetch available comprehensions (only if initialComprehension is not provided)
  useEffect(() => {
    // Determine if fetching is needed
    const shouldFetch = !initialComprehension && userData?.role !== 'teacher';

    if (shouldFetch) {
      const fetchComprehensions = async () => {
        setIsLoading(true);
        setError(null); // Clear previous errors
        try {
          const response = await fetch(`${API_BASE_URL}/api/comprehensions/available`, { 
            headers: {
              'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`
            }
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to fetch comprehensions');
          }

          const data = await response.json();
          setComprehensions(data);
        } catch (err) {
          logger.error('Error fetching comprehensions:', err);
          setError(err.message || 'Failed to load comprehensions');
        } finally {
          setIsLoading(false);
        }
      };

      fetchComprehensions();
    } else {
      // If not fetching, ensure loading is false
      setIsLoading(false);
    }

  }, [API_BASE_URL, authToken, userData?.role, initialComprehension]);

  // Initialize answers if initialComprehension is provided (MOVED BEFORE TEACHER CHECK)
  useEffect(() => {
    if (initialComprehension && initialComprehension.questions) {
      const initialAns = {};
      initialComprehension.questions.forEach((_, index) => {
        initialAns[index] = '';
      });
      setAnswers(initialAns);
    }
  }, [initialComprehension]);

  // If user is a teacher, show teacher view
  if (userData?.role === 'teacher') {
    // This component is primarily for students or teachers answering their own.
    // The main teacher view (listing, creating) is in TeacherComprehensionView.
    // If a teacher gets here via initialComprehension, they are answering.
    // If they somehow get here without initialComprehension, maybe show an error or redirect?
    // For now, assuming TeacherComprehensionView handles the main teacher workflow.
    // If a teacher IS answering (initialComprehension is set), the logic proceeds below.
    // If not, maybe return null or an error message? Let's return null for now.
    if (!initialComprehension) {
        logger.warn("ComprehensionView rendered for teacher without initialComprehension data.");
        return null; // Or some placeholder/error
    }
  }

  const handleComprehensionSelect = (comprehension) => {
    track('comprehension_selected', {
      comprehension_id: comprehension.id,
      comprehension_title: comprehension.title,
      is_public: comprehension.isPublic
    });
    // Ensure questions are parsed
    const parsedComp = {
        ...comprehension,
        questions: typeof comprehension.questions === 'string'
            ? JSON.parse(comprehension.questions)
            : comprehension.questions
    };
    setSelectedComprehension(parsedComp);
    // Initialize answers object
    const initialAnswers = {};
    parsedComp.questions.forEach((_, index) => {
      initialAnswers[index] = '';
    });
    setAnswers(initialAnswers);
  };

  const handleAnswerChange = (questionIndex, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionIndex]: value
    }));
  };

  const handleImageSelect = async (questionIndex, event) => {
    const file = event.target.files[0];
    if (!file) return;

    track('comprehension_image_upload_started', {
      question_index: questionIndex,
      file_type: file.type
    });

    try {
      // Show processing state
      setProcessingImages(prev => ({
        ...prev,
        [questionIndex]: true
      }));

      // Store file for preview
      setImageFiles(prev => ({
        ...prev,
        [questionIndex]: file
      }));

      // Create form data
      const formData = new FormData();
      formData.append('images', file);

      // Send for OCR processing
      const response = await fetch(`${API_BASE_URL}/api/ocr-images`, {
        method: 'POST',
        headers: {
          'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to process image');
      }

      const data = await response.json();
      
      track('comprehension_image_upload_success', {
        question_index: questionIndex,
        task_id: data.task_id
      });

      // Start polling for OCR results
      if (data.task_id) {
        pollOCRStatus(data.task_id, questionIndex);
      }
    } catch (err) {
      track('comprehension_image_upload_error', {
        question_index: questionIndex,
        error: err.message
      });
      logger.error('Error processing image:', err);
      setError('Failed to process image. Please try again or enter text manually.');
      setProcessingImages(prev => {
        const newProcessing = { ...prev };
        delete newProcessing[questionIndex];
        return newProcessing;
      });
    }
  };

  const pollOCRStatus = async (taskId, questionIndex) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ocr-status/${taskId}`, {
        headers: {
          'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to check OCR status');
      }

      const data = await response.json();
      
      if (data.state === 'SUCCESS') {
        // Update answers with OCR result
        setAnswers(prev => ({
          ...prev,
          [questionIndex]: data.result.response
        }));
        setProcessingImages(prev => {
          const newProcessing = { ...prev };
          delete newProcessing[questionIndex];
          return newProcessing;
        });
      } else if (data.state === 'FAILURE') {
        throw new Error('OCR processing failed');
      } else {
        // Still processing, poll again after delay
        setTimeout(() => pollOCRStatus(taskId, questionIndex), 2000);
      }
    } catch (err) {
      logger.error('Error polling OCR status:', err);
      setError('Failed to process image. Please try again or enter text manually.');
      setProcessingImages(prev => {
        const newProcessing = { ...prev };
        delete newProcessing[questionIndex];
        return newProcessing;
      });
    }
  };

  const handleRemoveImage = (questionIndex) => {
    setImageFiles(prev => {
      const newFiles = { ...prev };
      delete newFiles[questionIndex];
      return newFiles;
    });
  };

  const pollSubmissionStatus = async (subId) => {
    try {
      const statusResponse = await fetch(`${API_BASE_URL}/api/submissions/${subId}/status`, {
        headers: {
          'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`
        }
      });

      if (!statusResponse.ok) {
        throw new Error('Failed to check submission status');
      }

      const statusData = await statusResponse.json();
      
      if (statusData.status === 'completed') {
        logger.info('Status is completed, fetching full details...');
        try {
            // Fetch full details now that it's complete
            const detailsResponse = await fetch(`${API_BASE_URL}/api/submissions/${subId}/details`, { 
                headers: {
                  'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`
                }
            });
            if (!detailsResponse.ok) {
                const errorData = await detailsResponse.json();
                throw new Error(errorData.error || 'Failed to fetch submission details');
            }
            const fullSubmissionData = await detailsResponse.json();

            // Now we have the full data needed for the results view
            setSubmissionStatus(fullSubmissionData); // Update local status with full data
            setIsLoading(false); 
            setSuccess(true); 
            setLoadingMessage(''); 

            if (onSubmissionComplete) {
              logger.info("Polling complete, calling onSubmissionComplete with full details:", fullSubmissionData);
              onSubmissionComplete(fullSubmissionData); // Pass FULL details
            }
        } catch (detailsError) {
            logger.error('Error fetching submission details:', detailsError);
            setError('Failed to load submission results.');
            setIsLoading(false);
            setLoadingMessage('');
        }
      } else {
        // Continue polling if not complete
        setLoadingMessage('Grading your answers...'); 
        setTimeout(() => pollSubmissionStatus(subId), 2000); 
      }
    } catch (err) {
      logger.error('Error polling submission status:', err);
      setError('Failed to check submission status. Please try again.');
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    track('comprehension_submit_started', {
      comprehension_id: selectedComprehension.id,
      comprehension_title: selectedComprehension.title,
      question_count: Object.keys(answers).length
    });

    setIsLoading(true);
    setError(null);
    setSuccess(false);
    setLoadingMessage('Submitting your answers...');

    try {
      const response = await fetch(`${API_BASE_URL}/api/comprehensions/${selectedComprehension.id}/submit`, {
        method: 'POST',
        headers: {
          'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          answers: Object.values(answers)
        })
      });

      if (!response.ok) {
        track('comprehension_submit_error', {
          comprehension_id: selectedComprehension.id,
          error: 'API Error',
          status: response.status
        });
        throw new Error('Failed to submit answers');
      }

      const result = await response.json();
      if (result.submission_id) {
        track('comprehension_submit_success', {
          comprehension_id: selectedComprehension.id,
          submission_id: result.submission_id
        });
        setSubmissionId(result.submission_id);
        // Start polling for status
        pollSubmissionStatus(result.submission_id);
      } else {
        throw new Error('No submission ID received');
      }
    } catch (err) {
      track('comprehension_submit_error', {
        comprehension_id: selectedComprehension.id,
        error: err.message
      });
      setError(err.message);
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  // Add loading state for submission polling
  if (submissionStatus && submissionStatus !== 'complete' && submissionStatus !== 'error') {
    return <LoadingAnecdotes isUKUser={isUKUser} />;
  }

  // Main loading state - use !selectedComprehension as original
  if (isLoading && !selectedComprehension) {
    return (
      <div className="comprehension-view loading-view">
        <LoadingAnecdotes isUKUser={isUKUser} />
      </div>
    );
  }

  if (error && !selectedComprehension) {
    return <div className="error-message">{error}</div>;
  }

  // Filter comprehensions for display
  const publicComprehensions = comprehensions
    .filter(comp => comp.isPublic)
    .sort((a, b) => b.title.localeCompare(a.title)); // Sort by title descending

  const teacherComprehensions = comprehensions
    .filter(comp => !comp.isPublic)
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at)); // Sort by created_at ascending

  if (!selectedComprehension) {
    return (
      <div className="comprehension-selection-view">
        <h2>Choose a Comprehension</h2>

        <div className="comprehension-panel">
          <h3 onClick={() => setIsPastExamsCollapsed(!isPastExamsCollapsed)} className="collapsible-header">
            Past Exams
            <span className={`toggle-icon ${isPastExamsCollapsed ? 'collapsed' : ''}`}>{isPastExamsCollapsed ? '▼' : '▲'}</span>
          </h3>
          {!isPastExamsCollapsed && (
            <>
              {publicComprehensions.length === 0 ? (
                <p>No past exams available.</p>
              ) : (
                <div className="comprehension-grid">
                  {publicComprehensions.map(comp => (
                    <div 
                      key={comp.id} 
                      className="comprehension-card"
                      onClick={() => handleComprehensionSelect(comp)}
                    >
                      <h4>{comp.title}</h4>
                      <p className="teacher">Source: Public</p>
                      <button className="start-button">Start</button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="comprehension-panel">
          <h3 onClick={() => setIsTeacherExamsCollapsed(!isTeacherExamsCollapsed)} className="collapsible-header">
            From My Teacher
            <span className={`toggle-icon ${isTeacherExamsCollapsed ? 'collapsed' : ''}`}>{isTeacherExamsCollapsed ? '▼' : '▲'}</span>
          </h3>
          {!isTeacherExamsCollapsed && (
            <>
              {teacherComprehensions.length === 0 ? (
                <p>No assignments from your teacher.</p>
              ) : (
                <div className="comprehension-grid">
                  {teacherComprehensions.map(comp => (
                    <div 
                      key={comp.id} 
                      className="comprehension-card"
                      onClick={() => handleComprehensionSelect(comp)}
                    >
                      <h4>{comp.title}</h4>
                      <p className="teacher">By: {comp.teacherName}</p>
                      <button className="start-button">Start</button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="comprehension-view">
      {isLoading && <LoadingAnecdotes />}
      <button 
        className="back-button"
        onClick={() => {
          if (onBack) {
            onBack();
          } else {
            setSelectedComprehension(null);
            setError(null);
            setSuccess(false);
            setImageFiles({});
            setAnswers({});
            setSubmissionId(null);
            setSubmissionStatus(null);
            setLoadingMessage('');
          }
        }}
      >
        ← Back
      </button>

      <h2>{selectedComprehension.title}</h2>
      
      {error && <div className="error-message">{error}</div>}
      {success && submissionStatus && (
        <div className="success-message">
          <p>Answers submitted and graded successfully!</p>
          <p>Your score: {submissionStatus.score}</p>
        </div>
      )}
      {loadingMessage && (
        <div className="loading-message">
          <div className="spinner"></div>
          <p>{loadingMessage}</p>
        </div>
      )}

      <div className="comprehension-text">
        {selectedComprehension.text}
      </div>

      <form onSubmit={handleSubmit} className="questions-form">
        <h3>Questions</h3>
        {selectedComprehension.questions.map((question, index) => (
          <div key={index} className="question-group">
            <label>
              <div className="question-header">
                <span className="question-number">Question {index + 1}</span>
                <span className="points">({question.points} points)</span>
              </div>
              <p className="question-text">{question.text}</p>
              <div className="answer-input-group">
                <textarea
                  value={answers[index] || ''}
                  onChange={(e) => handleAnswerChange(index, e.target.value)}
                  placeholder="Write your answer here or upload an image..."
                  
                  disabled={processingImages[index]}
                />
                <div className="image-upload">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageSelect(index, e)}
                    id={`image-upload-${index}`}
                    className="hidden"
                    disabled={processingImages[index]}
                  />
                  <label 
                    htmlFor={`image-upload-${index}`} 
                    className={`upload-button ${processingImages[index] ? 'disabled' : ''}`}
                  >
                    📷 Upload Image
                  </label>
                  {imageFiles[index] && (
                    <div className="image-preview">
                      <img
                        src={URL.createObjectURL(imageFiles[index])}
                        alt={`Preview for question ${index + 1}`}
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index)}
                        className="remove-image"
                        disabled={processingImages[index]}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                  {processingImages[index] && (
                    <div className="processing-indicator">
                      <div className="spinner"></div>
                      <span>Processing image...</span>
                    </div>
                  )}
                </div>
              </div>
            </label>
          </div>
        ))}

        <button
          type="submit"
          className="submit-button"
          disabled={isLoading || Object.keys(processingImages).length > 0}
        >
          {loadingMessage || 
           (Object.keys(processingImages).length > 0 ? 'Processing Images...' : 
           'Submit Answers')}
        </button>
      </form>
    </div>
  );
}

export default ComprehensionView; 